// CompleteStep.js - Final step: Celebration and call-to-action
import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Animated } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../i18n/useTranslation';

const CompleteStep = ({ userName, onStartLearning, onTakeTour }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const firstName = userName?.split(' ')[0] || t('there');

  // Animation values
  const scaleAnim = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const confettiAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // Entrance animation sequence
    Animated.sequence([
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 50,
        friction: 7,
        useNativeDriver: true,
      }),
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }),
    ]).start();

    // Continuous confetti animation
    Animated.loop(
      Animated.sequence([
        Animated.timing(confettiAnim, {
          toValue: 1,
          duration: 1500,
          useNativeDriver: true,
        }),
        Animated.timing(confettiAnim, {
          toValue: 0,
          duration: 1500,
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, []);

  const colors = {
    bg: isDark ? '#1e293b' : '#f8fafc',
    card: isDark ? '#334155' : '#ffffff',
    text: isDark ? '#f8fafc' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    accent: '#2563eb',
    accentLight: isDark ? '#2563eb30' : '#2563eb15',
    border: isDark ? '#475569' : '#e2e8f0',
    success: '#10b981',
    warning: '#f59e0b',
    purple: '#8b5cf6',
  };

  const confettiRotate = confettiAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '15deg'],
  });

  return (
    <View style={[styles.container, { backgroundColor: colors.bg }]}>
      <View style={[styles.content, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Celebration icon with animation */}
        <Animated.View
          style={[
            styles.celebrationContainer,
            {
              transform: [{ scale: scaleAnim }],
            },
          ]}
        >
          {/* Confetti icons */}
          <Animated.View
            style={[
              styles.confettiLeft,
              { transform: [{ rotate: confettiRotate }] },
            ]}
          >
            <MaterialCommunityIcons name="star-four-points" size={24} color={colors.warning} />
          </Animated.View>
          <Animated.View
            style={[
              styles.confettiRight,
              { transform: [{ rotate: confettiRotate }] },
            ]}
          >
            <MaterialCommunityIcons name="star-four-points" size={20} color={colors.purple} />
          </Animated.View>

          <View style={[styles.iconContainer, { backgroundColor: colors.success + '20' }]}>
            <MaterialCommunityIcons name="party-popper" size={56} color={colors.success} />
          </View>
        </Animated.View>

        {/* Title */}
        <Text style={[styles.title, { color: colors.text }]}>
          {t("You're all set!")}
        </Text>

        {/* Personalized message */}
        <Animated.View style={{ opacity: fadeAnim }}>
          <Text style={[styles.message, { color: colors.textSecondary }]}>
            {t('Great job, {name}! Your personalized learning path is ready.').replace('{name}', firstName)}
          </Text>

          {/* Summary illustration */}
          <View style={[styles.summaryBox, { backgroundColor: colors.bg, borderColor: colors.border }]}>
            <View style={styles.summaryRow}>
              <MaterialCommunityIcons name="check-circle" size={20} color={colors.success} />
              <Text style={[styles.summaryText, { color: colors.text }]}>
                {t('Profile created')}
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <MaterialCommunityIcons name="check-circle" size={20} color={colors.success} />
              <Text style={[styles.summaryText, { color: colors.text }]}>
                {t('Subjects selected')}
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <MaterialCommunityIcons name="check-circle" size={20} color={colors.success} />
              <Text style={[styles.summaryText, { color: colors.text }]}>
                {t('Learning goal set')}
              </Text>
            </View>
          </View>
        </Animated.View>

        {/* Action Buttons */}
        <Animated.View style={[styles.buttonContainer, { opacity: fadeAnim }]}>
          {/* Optional tour button */}
          {onTakeTour && (
            <TouchableOpacity
              style={[styles.secondaryButton, { borderColor: colors.border }]}
              onPress={onTakeTour}
              activeOpacity={0.7}
            >
              <MaterialCommunityIcons name="compass-outline" size={20} color={colors.text} />
              <Text style={[styles.secondaryButtonText, { color: colors.text }]}>
                {t('Take Quick Tour')}
              </Text>
            </TouchableOpacity>
          )}

          {/* Main CTA */}
          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.accent }]}
            onPress={onStartLearning}
            activeOpacity={0.8}
          >
            <MaterialCommunityIcons name="rocket-launch" size={20} color="#fff" />
            <Text style={styles.primaryButtonText}>{t('Start Learning')}</Text>
          </TouchableOpacity>
        </Animated.View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 16,
    padding: 32,
    alignItems: 'center',
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  celebrationContainer: {
    position: 'relative',
    marginBottom: 24,
  },
  confettiLeft: {
    position: 'absolute',
    top: -10,
    left: -20,
  },
  confettiRight: {
    position: 'absolute',
    top: 0,
    right: -15,
  },
  iconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
  },
  message: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
    paddingHorizontal: 8,
  },
  summaryBox: {
    width: '100%',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 28,
    gap: 12,
  },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  summaryText: {
    fontSize: 14,
    fontWeight: '500',
  },
  buttonContainer: {
    width: '100%',
    gap: 12,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  secondaryButtonText: {
    fontSize: 15,
    fontWeight: '500',
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 10,
    gap: 8,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default CompleteStep;
